import PortalLayout from '@/components/layout/portal/PortalLayout';
import { useAppSelector } from '@/state/hooks';
import { selectCredentials, selectCurrentAccount } from '@/state/reducers/authSlice';
import { Fragment, useEffect, useMemo, useState } from 'react';

import PortalTradesTable from '@/components/portal-modules/PortalTradesTable';
import { getOperationalCurrentTradeDate } from '@/helpers/dateHelper';
import { convertToTradeBookingRow, TradeBookingRow } from '@/helpers/portalTradesHelper';
import { TradesResponse } from '@/types/sso';
import { TableFilter } from '@/types/table';
import Unauthorized401 from '@/Unauthorized401';
import useLanguage from '@/utils/hooks/useLanguage';
import usePortalAuthentication from '@/utils/hooks/usePortalAuthentication';
import { useTradesFilter } from '@/utils/hooks/useTradesFilter';
import { useScreenSizes } from '@/utils/providers/ScreenSizesProvider';
import { useSso } from '@/utils/providers/SSOProvider';
import { useDateFormatter, useLocale } from 'react-aria';
import Skeleton from 'react-loading-skeleton';

const cardHeight = 122;
const tableHeight = 204.2;

export interface TradesFilters {
    startDate: TableFilter<Date>;
    endDate: TableFilter<Date>;
}

const currentTradeDate = getOperationalCurrentTradeDate();

function PortalTrades() {
    usePortalAuthentication();

    const lang = useLanguage();
    const screen = useScreenSizes();

    const credentials = useAppSelector(selectCredentials);
    const currentAccount = useAppSelector(selectCurrentAccount);

    const { foundEntity, instrumentsApi, hasPermission } = useSso();

    const defaultFilters = useMemo(
        () => ({
            startDate: {
                value: currentTradeDate,
                label: lang.common.startDate
            },
            endDate: {
                value: currentTradeDate,
                label: lang.common.endDate
            }
        }),
        [lang, currentTradeDate]
    );

    const { locale } = useLocale();

    const dateFormatter = useDateFormatter({ timeZone: 'UTC' });
    const executionTimeFormatter = useDateFormatter({ dateStyle: 'short', timeStyle: 'medium' });

    const [isInitialMount, setIsInitialMount] = useState<boolean>(true);
    const [filters, setFilters] = useState<TradesFilters>(defaultFilters);

    const { startDate, endDate } = useMemo(() => filters, [filters]);
    const legalEntityId = useMemo(() => {
        const found = foundEntity?.addresses?.find((address) => address.type === 'LEI')?.value;
        return found ? found : '-';
    }, [foundEntity]);

    const {
        data: tradeBookings,
        isLoading: isTradeBookingsLoading,
        isValidating: isTradeBookingsValidating,
        mutate: refreshAllTradeBookings
    } = useTradesFilter({
        startDate,
        endDate
    });

    const { data: instruments } = instrumentsApi;

    const tradeBookingData: TradeBookingRow[] = useMemo(() => {
        if (tradeBookings && tradeBookings?.length > 0 && instruments) {
            const returnValues: TradesResponse[] = tradeBookings.filter((trade) => trade.type !== 'FxOptionTrade');

            returnValues.sort((a, b) => b.tradeId - a.tradeId);

            return returnValues.map((trade) =>
                convertToTradeBookingRow(
                    trade,
                    legalEntityId,
                    dateFormatter,
                    executionTimeFormatter,
                    instruments,
                    locale
                )
            );
        } else return [];
    }, [tradeBookings, legalEntityId, dateFormatter, executionTimeFormatter, instruments, locale]);

    useEffect(() => {
        refreshAllTradeBookings();
    }, [filters.startDate, filters.endDate]);

    useEffect(() => {
        refreshAllTradeBookings();
        setIsInitialMount(false);
    }, [currentAccount]);

    const isAuthorized = hasPermission('ACCOUNT_VIEW');

    return credentials ? (
        <PortalLayout isAuthorized={isAuthorized}>
            {isAuthorized && (
                <div className="flex flex-col w-full gap-2 sm:gap-4 p-2 sm:p-6 text-brand-text mt-16 lg:mt-0 max-h-full">
                    <div className="flex flex-wrap md:h-[34px] gap-2 w-full justify-between">
                        <div className="font-bold text-xl">{lang.navigation.trades}</div>
                        {/* <Button
                                type="submit"
                                className="h-fit w-fit !gap-2 text-xs"
                                onClick={exportDisclosure[1].open}>
                                <MdOutlineDocumentScanner className="w-4 h-4" />
                                Export Trades
                            </Button> */}
                    </div>
                    {isInitialMount && (isTradeBookingsLoading || isTradeBookingsValidating) ? (
                        <Fragment>
                            {screen.mobile ? (
                                <div className="flex flex-col w-full gap-2 sm:gap-4">
                                    <Skeleton containerClassName="flex-1" height={cardHeight} />
                                    {/* <Skeleton containerClassName="flex-1" height={cardHeight} />
                                        <Skeleton containerClassName="flex-1" height={cardHeight} /> */}
                                </div>
                            ) : (
                                <div className="flex flex-wrap w-full gap-2 sm:gap-4">
                                    {/* <div className="flex flex-wrap w-full gap-2 sm:gap-4">
                                            <Skeleton containerClassName="flex-1" height={cardHeight} />
                                            <Skeleton containerClassName="flex-1" height={cardHeight} />
                                            <Skeleton containerClassName="flex-1" height={cardHeight} />
                                        </div> */}
                                    {/* <div className="flex w-full">
                                            <Skeleton containerClassName="flex-1" height={tableHeight} />
                                        </div> */}
                                    <div className="flex w-full">
                                        <Skeleton containerClassName="flex-1" height={tableHeight} />
                                    </div>
                                </div>
                            )}
                        </Fragment>
                    ) : (
                        <PortalTradesTable
                            data={tradeBookingData}
                            filters={filters}
                            setFilters={setFilters}
                            defaultFilters={defaultFilters}
                            isLoading={isTradeBookingsLoading || isTradeBookingsValidating}
                            mutate={refreshAllTradeBookings as any}
                        />
                    )}
                </div>
            )}
            {!isAuthorized && <Unauthorized401 />}
            {/* <ExportTradesModal opened={exportDisclosure[0]} handlers={exportDisclosure[1]} /> */}
        </PortalLayout>
    ) : null;
}

export default PortalTrades;
